
import { type RouteObject, Navigate } from 'react-router-dom';
import { lazy } from 'react';
import ErrorBoundary from '../components/base/ErrorBoundary';

// Lazy load all route components for code splitting
const Home = lazy(() => import('../pages/home/page'));
const Auth = lazy(() => import('../pages/auth/page'));
const Profile = lazy(() => import('../pages/profile/page'));
const Settings = lazy(() => import('../pages/settings/page'));
const Search = lazy(() => import('../pages/search/page'));
const AdminDashboard = lazy(() => import('../pages/admin/page'));
const AdminLogin = lazy(() => import('../pages/admin/login/page'));
const ArticleEditor = lazy(() => import('../pages/admin/articles/editor/page'));
const PageTemplateEditor = lazy(() => import('../pages/admin/page-templates/PageTemplateEditor'));
const PageTemplatePreview = lazy(() => import('../pages/admin/page-templates/PageTemplatePreview'));
const PageView = lazy(() => import('../pages/page-templates/PageView'));
const ArticleDetail = lazy(() => import('../pages/articles/[slug]/page'));
const MyArticles = lazy(() => import('../pages/my/articles/page'));
const ArticleGallery = lazy(() => import('../pages/my/articles/gallery/page'));
const ImageDetail = lazy(() => import('../pages/my/articles/gallery/[id]/page'));
const ImageMarkingGallery = lazy(() => import('../pages/my/articles/image-marking/page'));
const ImageMarkingDetail = lazy(() => import('../pages/my/articles/image-marking/[id]/page'));
const QingshiHomeGallery = lazy(() => import('../pages/my/articles/qingshi-home/page'));
const QingshiHomeDetail = lazy(() => import('../pages/my/articles/qingshi-home/[id]/page'));
const UploadArticle = lazy(() => import('../pages/my/articles/upload/page'));
const DwgViewerPage = lazy(() => import('../pages/viewer/dwg/page'));
const UploadImage = lazy(() => import('../pages/my/articles/upload-image/page'));
const ArticleView = lazy(() => import('../pages/my/articles/view/[id]/page'));
const ArticleId = lazy(() => import('../pages/my/articles/[id]/page'));
const CreateArticle = lazy(() => import('../pages/my/articles/create/page'));
const EditArticle = lazy(() => import('../pages/my/articles/edit/[id]/page'));
const MyWorks = lazy(() => import('../pages/my/works/page'));
const MySpaces = lazy(() => import('../pages/my/spaces/page'));
const SpaceDetail = lazy(() => import('../pages/my/spaces/[id]/page'));
const CreateSpace = lazy(() => import('../pages/my/spaces/create/page'));
const EditSpace = lazy(() => import('../pages/my/spaces/edit/[id]/page'));
const MyTools = lazy(() => import('../pages/my/tools/page'));
const ExcelMatcher = lazy(() => import('../pages/my/tools/excel-matcher/page'));
const MyFavorites = lazy(() => import('../pages/my/favorites/page'));
const MyFollows = lazy(() => import('../pages/my/follows/page'));
const ImageCollections = lazy(() => import('../pages/my/image-collections/page'));
const MyMessages = lazy(() => import('../pages/my/messages/page'));
const MyOrders = lazy(() => import('../pages/my/orders/page'));
const MyShop = lazy(() => import('../pages/my/shop/page'));
const ShopOrders = lazy(() => import('../pages/my/shop/orders/page'));
const ShopAnalytics = lazy(() => import('../pages/my/shop/analytics/page'));
const ShopSettings = lazy(() => import('../pages/my/shop/settings/page'));
const ShopCases = lazy(() => import('../pages/my/shop/cases/page'));
const ShopWallet = lazy(() => import('../pages/my/shop/wallet/page'));
const ShopWalletWithdraw = lazy(() => import('../pages/my/shop/wallet/withdraw/page'));
const ShopWalletWithdrawals = lazy(() => import('../pages/my/shop/wallet/withdrawals/page'));
const ShopPreview = lazy(() => import('../pages/shop/preview/page'));
const DesignService = lazy(() => import('../pages/service/design/page'));
const WatermarkService = lazy(() => import('../pages/service/watermark/page'));
const UnifiedGallery = lazy(() => import('../pages/gallery/page'));
const WorkDetail = lazy(() => import('../pages/works/[id]/page'));
const MerchantsPage = lazy(() => import('../pages/merchants/page'));
const MerchantDetailPage = lazy(() => import('../pages/merchants/[id]/page'));
const MerchantRegisterPage = lazy(() => import('../pages/merchants/register/page'));
const MyRegisteredMerchantsPage = lazy(() => import('../pages/merchants/my-registered/page'));
const PlatformAuthPage = lazy(() => import('../pages/my/platform-auth/page'));
const MembershipPage = lazy(() => import('../pages/my/membership/page'));
const AIChatPage = lazy(() => import('../pages/ai-chat/page'));
const NotFound = lazy(() => import('../pages/NotFound'));
import AdminRoute from '../components/admin/AdminRoute';
import ProtectedRoute from '../components/common/ProtectedRoute';

const routes: RouteObject[] = [
  {
    path: '/',
    element: <Home />
  },
  {
    path: '/auth',
    element: <Auth />
  },
  // 兼容旧链接与 SSO 历史跳转：/login、/auth/login → 登录页
  {
    path: '/login',
    element: <Auth />
  },
  {
    path: '/auth/login',
    element: <Auth />
  },
  {
    path: '/profile',
    element: (
      <ProtectedRoute>
        <Profile />
      </ProtectedRoute>
    )
  },
  {
    path: '/settings',
    element: (
      <ProtectedRoute>
        <Settings />
      </ProtectedRoute>
    )
  },
  {
    path: '/search',
    element: <Search />
  },
  {
    path: '/admin',
    element: (
      <AdminRoute>
        <AdminDashboard />
      </AdminRoute>
    )
  },
  {
    path: '/admin/login',
    element: <AdminLogin />
  },
  {
    path: '/admin/articles/editor/:id',
    element: (
      <AdminRoute requiredPermission="articles">
        <ArticleEditor />
      </AdminRoute>
    )
  },
  {
    path: '/admin/page-templates/:id/edit',
    element: (
      <AdminRoute requiredPermission="content">
        <PageTemplateEditor />
      </AdminRoute>
    )
  },
  {
    path: '/admin/page-templates/:id/preview',
    element: (
      <AdminRoute requiredPermission="content">
        <PageTemplatePreview />
      </AdminRoute>
    )
  },
  {
    path: '/page-templates/:slug',
    element: <PageView />
  },
  {
    path: '/articles/:slug',
    element: <ArticleDetail />
  },
  {
    path: '/my/articles',
    element: (
      <ProtectedRoute>
        <MyArticles />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/articles/gallery',
    element: <ArticleGallery />
  },
  {
    path: '/my/articles/gallery/:id',
    element: <ImageDetail />
  },
  {
    path: '/my/articles/image-marking',
    element: <ImageMarkingGallery />
  },
  {
    path: '/my/articles/image-marking/:id',
    element: <ImageMarkingDetail />
  },
  {
    path: '/my/articles/qingshi-home',
    element: <QingshiHomeGallery />
  },
  {
    path: '/my/articles/qingshi-home/:id',
    element: <QingshiHomeDetail />
  },
  {
    path: '/my/articles/upload',
    element: (
      <ProtectedRoute>
        <UploadArticle />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/articles/upload-image',
    element: (
      <ProtectedRoute>
        <UploadImage />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/articles/view/:id',
    element: (
      <ProtectedRoute>
        <ArticleView />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/articles/:id',
    element: (
      <ProtectedRoute>
        <ArticleId />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/articles/create',
    element: (
      <ProtectedRoute>
        <CreateArticle />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/articles/edit/:id',
    element: (
      <ProtectedRoute>
        <EditArticle />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/works',
    element: (
      <ProtectedRoute>
        <MyWorks />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/spaces',
    element: (
      <ProtectedRoute>
        <MySpaces />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/spaces/:id',
    element: (
      <ProtectedRoute>
        <SpaceDetail />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/spaces/create',
    element: (
      <ProtectedRoute>
        <CreateSpace />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/spaces/edit/:id',
    element: (
      <ProtectedRoute>
        <EditSpace />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/tools',
    element: <MyTools />
  },
  {
    path: '/my/tools/excel-matcher',
    element: (
      <ProtectedRoute>
        <ExcelMatcher />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/favorites',
    element: (
      <ProtectedRoute>
        <MyFavorites />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/follows',
    element: (
      <ProtectedRoute>
        <MyFollows />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/image-collections',
    element: (
      <ProtectedRoute>
        <ImageCollections />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/messages',
    element: (
      <ProtectedRoute>
        <MyMessages />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/orders',
    element: (
      <ProtectedRoute>
        <MyOrders />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/shop',
    element: (
      <ProtectedRoute>
        <MyShop />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/shop/orders',
    element: (
      <ProtectedRoute>
        <ShopOrders />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/shop/analytics',
    element: (
      <ProtectedRoute>
        <ShopAnalytics />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/shop/settings',
    element: (
      <ProtectedRoute>
        <ShopSettings />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/shop/cases',
    element: (
      <ProtectedRoute>
        <ShopCases />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/shop/wallet',
    element: (
      <ProtectedRoute>
        <ShopWallet />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/shop/wallet/withdraw',
    element: (
      <ProtectedRoute>
        <ShopWalletWithdraw />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/shop/wallet/withdrawals',
    element: (
      <ProtectedRoute>
        <ShopWalletWithdrawals />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/platform-auth',
    element: (
      <ProtectedRoute>
        <PlatformAuthPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/my/membership',
    element: (
      <ProtectedRoute>
        <MembershipPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/shop/preview/:code',
    element: (
      <ErrorBoundary>
        <ShopPreview />
      </ErrorBoundary>
    )
  },
  {
    path: '/shop/preview/user/:userId',
    element: (
      <ErrorBoundary>
        <ShopPreview />
      </ErrorBoundary>
    )
  },
  {
    path: '/service/design',
    element: <DesignService />
  },
  {
    path: '/service/watermark',
    element: <WatermarkService />
  },
  {
    path: '/gallery',
    element: <UnifiedGallery />
  },
  {
    path: '/viewer/dwg',
    element: <DwgViewerPage />
  },
  {
    path: '/works/:id',
    element: <WorkDetail />
  },
  // 旧路由重定向（保持兼容性）
  {
    path: '/works/gallery',
    element: <UnifiedGallery />
  },
  {
    path: '/merchants',
    element: <MerchantsPage />
  },
  {
    path: '/merchants/register',
    element: <MerchantRegisterPage />
  },
  {
    path: '/merchants/my-registered',
    element: (
      <ProtectedRoute>
        <MyRegisteredMerchantsPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/merchants/:code',
    element: <MerchantDetailPage />
  },
  {
    path: '/ai-chat',
    element: (
      <ProtectedRoute>
        <AIChatPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/ai-chat/:conversationId',
    element: (
      <ProtectedRoute>
        <AIChatPage />
      </ProtectedRoute>
    )
  },
  {
    path: '*',
    element: <NotFound />
  }
];

export default routes;
